import { useCallback, useEffect, useRef } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useRouter } from 'next/router';
import { CreateEmployerRestaurantPayload } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';
import { JPOnboardingType } from '../job-position-onboarding/types';
import { JPOnboardingSteps, jpOnboardingStepsAtom } from '../job-position-onboarding/use-job-positions-onboarding';
import {
  activeRestaurantOnboardingStateAtom,
  RestaurantOnboardingStage,
} from '../restaurants/restaurant-onboarding-modal';
import { clearOtpCorrelationId } from './use-otp-correlation-id';

// ==================== TYPES ====================

export type OrgOnboardingSteps = 'restaurant_creation' | 'job_position_creation' | 'user_contact_details';

// Steps where the JP sub-step flow is embedded. They reuse the
// JobPositionCreationStep component and keep their sub-step in
// jpOnboardingStepsAtom instead of activeRestaurantOnboardingStateAtom.
export const JP_LIKE_STEPS: OrgOnboardingSteps[] = ['job_position_creation'];

// The ordered list of steps for the organization onboarding flow:
// venue first, then one JP step that runs every sub-step from role through
// review, then the user contact details.
export const ORG_ONBOARDING_STEPS: OrgOnboardingSteps[] = [
  'restaurant_creation',
  'job_position_creation',
  'user_contact_details',
];

// Default first sub-step for each org onboarding step
export const ORG_DEFAULT_SUB_STEPS: Record<OrgOnboardingSteps, string> = {
  // First step lands directly on the Google venue search.
  restaurant_creation: 'select_restaurant_from_google',
  job_position_creation: 'role',
  user_contact_details: 'restaurant_owner_onboarding_form',
};

// User contact details collected during org onboarding
export interface OrgOnboardingContactDetails {
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  privacy_policy: boolean;
  terms_and_conditions: boolean;
  vexatious_clauses_acceptance: boolean;
}

// Formatted restaurant data structure used during org onboarding
// This matches the payload structure created by useFormatRestaurantOnboardingData
export interface FormattedOrgRestaurantData {
  employer_user_contacts: {
    name: string;
    surname: string;
    email: string;
    phone_number: string;
  };
  restaurants: CreateEmployerRestaurantPayload;
}

// The full payload for creating all entities at once
export interface OrgOnboardingPayload {
  restaurant: CreateEmployerRestaurantPayload | null;
  job_position: Partial<JPOnboardingType> | null;
  contact_details: OrgOnboardingContactDetails | null;
}

// ==================== ATOMS ====================

// Referral info resolved from ?ref= URL param
export interface OrgOnboardingReferralInfo {
  referral_code: string;
  referrer_restaurant_name: string;
  referrer_organization_id: string;
  // Free days granted to the invited organization; absent on an older backend.
  credits_invitee?: number;
}

// What the backend already knows about a prospect arriving from an accepted referral
// invite, so the onboarding form does not ask for it twice.
//
// `venue` seeds the search box on the first step and is only a hint — the user still picks
// the Google suggestion themselves. `email` is filled in later, once they have selected a
// venue whose place id matches the referral request: the referral code alone is never
// enough, because it travels in an email that can be forwarded and a prefilled contact
// form is exactly where a wrong address goes unnoticed.
export interface OrgOnboardingReferralPrefill {
  venue?: {
    name: string;
    address: string | null;
  };
  email?: string;
}

// Partner info resolved from the ?partner= URL param. The code can resolve to a
// B2B ambassador ("procacciatore"), a worker enrolled in the "porta un locale"
// program, or a static acquisition campaign.
// Drives the free-days intro screen only; the partner_code itself travels to the
// create-org payload via useUTMSource (prospect tracking).
export interface OrgOnboardingPartnerInfo {
  partner_code: string;
  // Which kind of code resolved. Drives the intro screen variant: 'campaign'
  // has its own copy, while 'worker' shares the referral variant with
  // 'ambassador' (both are "you were invited by <name>").
  source_type: 'ambassador' | 'campaign' | 'worker';
  // Free PPU days granted by this code (backend-driven, per-campaign).
  free_days: number;
  // Ambassador display name (source_type 'ambassador').
  partner_name?: string;
  // Campaign display name (source_type 'campaign'); may be null.
  campaign_name?: string | null;
}

// LocalStorage keys for persistence
export const ORG_ONBOARDING_STORAGE_KEYS = {
  step: 'org-onboarding-step',
  restaurant: 'org-onboarding-restaurant',
  jpData: 'org-onboarding-jp-data',
  jpStep: 'org-onboarding-jp-step',
  contact: 'org-onboarding-contact',
  existingUserFlow: 'org-onboarding-existing-user-flow',
  existingUserId: 'org-onboarding-existing-user-id',
  existingUserPhone: 'org-onboarding-existing-user-phone',
  conflictingFields: 'org-onboarding-conflicting-fields',
  aiValidationIds: 'org-onboarding-ai-validation-ids',
  referralInfo: 'org-onboarding-referral-info',
  referralPrefill: 'org-onboarding-referral-prefill',
  partnerInfo: 'org-onboarding-partner-info',
  prospectId: 'org-onboarding-prospect-id',
} as const;

// Current step in the organization onboarding flow (persisted to localStorage)
export const orgOnboardingStepAtom = atomWithStorage<OrgOnboardingSteps>(
  ORG_ONBOARDING_STORAGE_KEYS.step,
  'restaurant_creation',
  undefined,
  { getOnInit: true }
);

// Flag to indicate that we are in the organization onboarding flow
// This is a runtime flag (NOT persisted) — set on mount by onboarding.tsx.
// MUST default to false: the standalone JP onboarding / venue flows share the
// same components and rely on this being false unless the org flow opts in
// (a true default would make standalone JP creation skip its create API,
// flip the primary CTA, and use org-only routing — see use-job-positions-
// onboarding.ts and job-position-onboarding-footer.tsx).
export const isOrgOnboardingModeAtom = atom<boolean>(false);

// Stores the formatted restaurant data collected during venue creation step (persisted)
// Structure: { employer_user_contacts: {...}, restaurants: {...} }
export const orgOnboardingRestaurantDataAtom = atomWithStorage<FormattedOrgRestaurantData | null>(
  ORG_ONBOARDING_STORAGE_KEYS.restaurant,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the job position form data collected during JP creation step (persisted)
export const orgOnboardingJPDataAtom = atomWithStorage<Partial<JPOnboardingType> | null>(
  ORG_ONBOARDING_STORAGE_KEYS.jpData,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the current JP sub-step within the JP creation flow (persisted)
// e.g. 'role', 'salary', 'review' — so we can restore the exact JP step on refresh
export const orgOnboardingJPStepAtom = atomWithStorage<JPOnboardingSteps | null>(
  ORG_ONBOARDING_STORAGE_KEYS.jpStep,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the user contact details collected during the contact step (persisted)
export const orgOnboardingContactDetailsAtom = atomWithStorage<OrgOnboardingContactDetails | null>(
  ORG_ONBOARDING_STORAGE_KEYS.contact,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the binary pictures (File objects) uploaded during restaurant creation
// This is a COPY from binaryPicturesAtom captured when user proceeds from restaurant step
// Why separate atom? Because resetState() clears binaryPicturesAtom, but we need to preserve
// the pictures until final submission at user_contact_details step
// Note: This is NOT persisted (Files can't be serialized to localStorage)
// User must re-upload if they refresh during onboarding
export const orgOnboardingBinaryPicturesAtom = atom<{ url: string; file: File }[]>([]);

// Flag: user went through the existing user login flow (email conflict → login → return)
// Persisted so it survives the redirect to /login and back
export const orgOnboardingExistingUserFlowAtom = atomWithStorage<boolean>(
  ORG_ONBOARDING_STORAGE_KEYS.existingUserFlow,
  false,
  undefined,
  { getOnInit: true }
);

// Stores the existing user's ID from the conflict response (persisted for login redirect)
export const orgOnboardingExistingUserIdAtom = atomWithStorage<string | null>(
  ORG_ONBOARDING_STORAGE_KEYS.existingUserId,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the masked phone number from the conflict response (e.g. "***1234")
// Used to display which number received the OTP
export const orgOnboardingExistingUserPhoneAtom = atomWithStorage<string | null>(
  ORG_ONBOARDING_STORAGE_KEYS.existingUserPhone,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the conflicting fields from the 409 response (e.g. ["email"], ["phone_number"], or ["email", "phone_number"])
// Used to customize the conflict message shown to the user
export const orgOnboardingConflictingFieldsAtom = atomWithStorage<string[] | null>(
  ORG_ONBOARDING_STORAGE_KEYS.conflictingFields,
  null,
  undefined,
  { getOnInit: true }
);

// Stores AI validation record IDs returned from validate_ad calls during org onboarding.
// These are sent with the org create payload so the backend can link them to the created JP.
export const orgOnboardingAiValidationIdsAtom = atomWithStorage<string[]>(
  ORG_ONBOARDING_STORAGE_KEYS.aiValidationIds,
  [],
  undefined,
  { getOnInit: true }
);

// Stores the referral info resolved from ?ref= URL param (persisted)
// Contains the referral code, referrer restaurant name, and referrer organization ID
export const orgOnboardingReferralInfoAtom = atomWithStorage<OrgOnboardingReferralInfo | null>(
  ORG_ONBOARDING_STORAGE_KEYS.referralInfo,
  null,
  undefined,
  { getOnInit: true }
);

// Stores what we already know about a prospect arriving from an accepted referral invite
// (persisted). Written in two passes: the venue hint when ?ref= resolves, the email once the
// user has selected a venue that matches the referral request.
export const orgOnboardingReferralPrefillAtom = atomWithStorage<OrgOnboardingReferralPrefill | null>(
  ORG_ONBOARDING_STORAGE_KEYS.referralPrefill,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the partner (B2B ambassador) info resolved from ?partner= URL param (persisted).
// Used only to render the partner intro screen; the partner_code is forwarded to the
// create-org payload separately via useUTMSource.
export const orgOnboardingPartnerInfoAtom = atomWithStorage<OrgOnboardingPartnerInfo | null>(
  ORG_ONBOARDING_STORAGE_KEYS.partnerInfo,
  null,
  undefined,
  { getOnInit: true }
);

// Stores the prospect UUID generated on the frontend for tracking funnel drop-offs.
// Persisted so it survives page refreshes and is sent with the org create payload for deletion on conversion.
export const orgOnboardingProspectIdAtom = atomWithStorage<string | null>(
  ORG_ONBOARDING_STORAGE_KEYS.prospectId,
  null,
  undefined,
  { getOnInit: true }
);

// Flag: user clicked "save and continue later" from JP onboarding header within org onboarding
// Runtime only (NOT persisted) — controls which header text variant to show on the contact step
export const orgOnboardingSaveAndContinueLaterAtom = atom<boolean>(false);

// ==================== HOOK ====================

// ==================== URL HELPERS ====================

/**
 * Update the URL with org onboarding step and optional sub-step.
 * Uses shallow routing so Next.js doesn't re-render the page.
 *
 * @param replace - If true, uses router.replace (no new history entry).
 *                  Defaults to false (uses router.push for browser back support).
 */
const updateOrgOnboardingUrl = (
  router: ReturnType<typeof useRouter>,
  step: OrgOnboardingSteps,
  subStep?: string,
  replace = false
) => {
  const currentQuery = router.query as Record<string, string | undefined>;
  // Skip if URL already matches to avoid duplicate history entries
  if (currentQuery.step === step && currentQuery.sub_step === subStep) return;

  // Merge step/sub_step into existing query params — preserves gmaps_place_id and any other params
  const query: Record<string, string> = { ...currentQuery, step } as Record<string, string>;
  if (subStep) {
    query.sub_step = subStep;
  } else {
    // Explicitly delete stale sub_step — spreading currentQuery would carry it over
    // to the new step (e.g. sub_step=select_restaurant_from_google leaking into job_position_creation)
    delete query.sub_step;
  }
  const nav = replace ? router.replace : router.push;
  nav({ pathname: router.pathname, query }, undefined, { shallow: true });
};

/**
 * Hook for managing Organization Onboarding step state and progress.
 * Provides utility functions for step transitions and progress tracking.
 * Also syncs the current org step to URL query params (?step=X).
 */
export const useOrgOnboardingSteps = () => {
  const router = useRouter();
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const setCurrentStep = useSetAtom(orgOnboardingStepAtom);

  const activeSteps = ORG_ONBOARDING_STEPS;
  const currentStepIndex = activeSteps.indexOf(currentStep);

  // Navigate to the next step
  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < activeSteps.length) {
      const nextStep = activeSteps[nextIndex];
      setCurrentStep(nextStep);
      updateOrgOnboardingUrl(router, nextStep);
    }
  }, [currentStepIndex, setCurrentStep, router, activeSteps]);

  // Navigate to the previous step
  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = activeSteps[prevIndex];
      setCurrentStep(prevStep);
      updateOrgOnboardingUrl(router, prevStep);
    }
  }, [currentStepIndex, setCurrentStep, router, activeSteps]);

  // Navigate to a specific step, optionally setting the sub-step in the URL
  const goToStep = useCallback(
    (step: OrgOnboardingSteps, subStep?: string) => {
      setCurrentStep(step);
      updateOrgOnboardingUrl(router, step, subStep);
    },
    [setCurrentStep, router]
  );

  // Update the URL sub_step without changing the main step
  const updateSubStep = useCallback(
    (subStep: string) => {
      updateOrgOnboardingUrl(router, currentStep, subStep);
    },
    [router, currentStep]
  );

  // Get progress information
  const getProgressInfo = useCallback(() => {
    return {
      currentStep,
    };
  }, [currentStep]);

  // Determine if header should be shown
  const shouldShowHeader = true;

  // Reset the entire org onboarding state
  const resetOrgOnboarding = useCallback(() => {
    setCurrentStep(activeSteps[0]);
  }, [setCurrentStep, activeSteps]);

  return {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updateSubStep,
    getProgressInfo,
    shouldShowHeader,
    resetOrgOnboarding,
    activeSteps,
    isFirstStep: currentStep === activeSteps[0],
    isLastStep: currentStep === activeSteps[activeSteps.length - 1],
  };
};

// Transient sub-steps that should not remain in browser history.
// They are pushed normally on entry, but when the NEXT sub-step replaces them,
// pressing back skips the transient step entirely.
const TRANSIENT_SUB_STEPS = ['magic_ai_loading'];

/**
 * Hook to sync a sub-step value to the URL whenever it changes.
 * Used by step components (restaurant, contact details) to reflect
 * their internal activeUI state in the URL as ?sub_step=X.
 *
 * On initial mount, uses router.replace to amend the URL (no extra history entry)
 * since goToNextStep/goToPreviousStep already pushed the main step.
 * Subsequent changes use router.push so browser back/forward works.
 * When leaving a transient sub-step, the next sub-step uses router.replace
 * so the transient entry is overwritten in history.
 */
export const useOrgSubStepUrlSync = (subStep: string, ownerStep: OrgOnboardingSteps) => {
  const router = useRouter();
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const isInitialMount = useRef(true);
  const prevSubStepRef = useRef<string | null>(null);

  useEffect(() => {
    if (currentStep !== ownerStep) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSubStepRef.current = subStep;
      // On initial mount, only update URL if sub_step is not already set.
      // The parent (useOrgOnboardingUrlSync) may have already resolved the correct sub_step;
      // overriding it here with the atom default would cause a wrong redirect.
      const currentSubStep = router.query.sub_step as string | undefined;
      if (!currentSubStep) {
        updateOrgOnboardingUrl(router, currentStep, subStep, true);
      }
      return;
    }

    // When leaving a transient sub-step, use replace so it gets overwritten
    // in browser history (back button will skip it)
    const useReplace = TRANSIENT_SUB_STEPS.includes(prevSubStepRef.current || '');
    prevSubStepRef.current = subStep;
    updateOrgOnboardingUrl(router, currentStep, subStep, useReplace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStep, currentStep, ownerStep]);
};

/**
 * Minimal URL sync hook for org onboarding.
 *
 * Atom values (persisted in localStorage) are the source of truth.
 * - On first render / refresh: push atoms → URL (overwrite stale URL params).
 *   If no sub-step is found in atoms or URL, reset to the first sub-step.
 * - On subsequent URL changes (browser back/forward): sync URL → atoms.
 *
 * Should be called once in the top-level OrganizationOnboarding component.
 */
export const useOrgOnboardingUrlSync = () => {
  const router = useRouter();
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const setCurrentStep = useSetAtom(orgOnboardingStepAtom);
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const jpStep = useAtomValue(orgOnboardingJPStepAtom);
  const setJPStep = useSetAtom(jpOnboardingStepsAtom);
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const jpData = useAtomValue(orgOnboardingJPDataAtom);
  const contactDetails = useAtomValue(orgOnboardingContactDetailsAtom);
  const hasInitialized = useRef(false);

  // Effect 1: On mount, reconcile the persisted step atom and the URL query
  // params. Single source of truth for mount-time bootstrap so there is no
  // race with sibling effects trying to align step state.
  //
  // - If the persisted step isn't a known step (stale from an older code
  //   version), reset to the first step.
  // - If nothing is in progress (no persisted JP/restaurant/contact data),
  //   always land on the first step even if the persisted step is technically
  //   valid.
  useEffect(() => {
    if (!router.isReady || hasInitialized.current) return;
    hasInitialized.current = true;

    const urlGmapsPlaceId =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('gmaps_place_id') : null;

    const hasExistingData = !!(restaurantData || jpData || contactDetails);
    const stepIsKnown = ORG_ONBOARDING_STEPS.includes(currentStep);

    let effectiveStep = currentStep;
    if (!hasExistingData || !stepIsKnown) {
      effectiveStep = ORG_ONBOARDING_STEPS[0];
      setCurrentStep(effectiveStep);
    }

    let subStep: string;

    if (JP_LIKE_STEPS.includes(effectiveStep)) {
      // Use only the persisted JP step atom (jpStep). Do NOT fall back to
      // currentJPStep (jpOnboardingStepsAtom) because it's a non-persisted atom
      // that always resets to 'venue_selection' on refresh — which is wrong for
      // org onboarding where the JP flow starts at 'role'.
      subStep = jpStep || ORG_DEFAULT_SUB_STEPS[effectiveStep];
    } else if (effectiveStep === 'restaurant_creation') {
      if (urlGmapsPlaceId) {
        // gmaps_place_id present → skip search, go to AI loading
        subStep = 'magic_ai_loading';
      } else if (restaurantData) {
        // Persisted restaurant data → show form
        subStep = 'restaurant_onboarding_form';
      } else {
        // No venue yet → land directly on the Google search. (The search input
        // self-focuses on mount, so there's no intermediate screen.)
        subStep = 'select_restaurant_from_google';
      }
    } else {
      // user_contact_details — activeUI is shared and not step-specific-persisted,
      // so always reset to default sub-step on refresh
      subStep = ORG_DEFAULT_SUB_STEPS[effectiveStep];
    }

    // Sync the resolved sub-step back to UI atoms so components match
    if (JP_LIKE_STEPS.includes(effectiveStep)) {
      setJPStep(subStep as JPOnboardingSteps);
    } else {
      setActiveUI(subStep as RestaurantOnboardingStage);
    }

    updateOrgOnboardingUrl(router, effectiveStep, subStep, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  // Effect 2: On URL change (browser back/forward), sync URL → atoms
  useEffect(() => {
    if (!router.isReady || !hasInitialized.current) return;

    const { step, sub_step } = router.query as { step?: string; sub_step?: string };

    // Restrict accepted steps to the known steps so a manually-edited URL
    // (e.g. ?step=some_unknown_step) can't push the atom into an inconsistent
    // state.
    // Sync main step from URL
    if (step && ORG_ONBOARDING_STEPS.includes(step as OrgOnboardingSteps)) {
      const orgStep = step as OrgOnboardingSteps;
      setCurrentStep(orgStep);

      // Resolve sub-step: use URL value if present, otherwise reset to default.
      // Exception: when navigating back to restaurant_creation with existing persisted data
      // and no explicit sub_step, show the form view instead of the Google search default.
      const resolvedSubStep =
        sub_step ||
        (orgStep === 'restaurant_creation' && restaurantData
          ? 'restaurant_onboarding_form'
          : ORG_DEFAULT_SUB_STEPS[orgStep]);

      if (JP_LIKE_STEPS.includes(orgStep)) {
        setJPStep(resolvedSubStep as JPOnboardingSteps);
      } else {
        setActiveUI(resolvedSubStep as RestaurantOnboardingStage);
      }

      // If URL is missing sub_step, amend it (replace, no extra history entry)
      if (!sub_step) {
        updateOrgOnboardingUrl(router, orgStep, resolvedSubStep, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, router.isReady, setCurrentStep, setActiveUI, setJPStep]);
};

/**
 * Hook that fires `$pageview` on every onboarding step/sub-step transition.
 *
 * Why this exists: org onboarding URL updates use Next.js shallow routing, which
 * doesn't fire `Router.events.routeChangeComplete` — so the global PostHog page
 * tracker in `usePostHogProvider` never sees intra-onboarding URL changes. Without
 * this hook, the entire flow looks like a single `$pageview` in PostHog and no
 * stepwise funnel can be built.
 *
 * Why `useAnalytics().trackPHEvent` (not `posthog.capture` directly): see
 * `docs/claude/analytics.md`. On-mount captures race `posthog.init()` and are
 * silently dropped in production. `trackPHEvent` guards on a `ready` flag, and
 * its `useCallback` identity flips when init completes — listing it in the
 * effect deps causes the effect to re-run after init, recovering any missed
 * capture.
 */
export const useOnboardingPageviewSync = () => {
  const router = useRouter();
  // trackPHEvent (not trackEvent) — trackEvent is typed to the EmployerEventName
  // union and rejects PostHog system events like $pageview. Both are the same
  // underlying function with the same `ready` guard and useCallback identity.
  const { trackPHEvent } = useAnalytics();
  const step = router.query.step as string | undefined;
  const subStep = router.query.sub_step as string | undefined;

  useEffect(() => {
    if (!router.isReady || !step) return;
    trackPHEvent('$pageview', {
      $current_url: window.location.href,
      onboarding_step: step,
      onboarding_sub_step: subStep,
    });
  }, [step, subStep, router.isReady, trackPHEvent]);
};

/**
 * Hook to reset all org onboarding atoms to initial state.
 * Also clears localStorage persistence. Should be called on success or explicit exit.
 */
export const useOrgOnboardingReset = () => {
  const setStep = useSetAtom(orgOnboardingStepAtom);
  const setIsOrgMode = useSetAtom(isOrgOnboardingModeAtom);
  const setRestaurantData = useSetAtom(orgOnboardingRestaurantDataAtom);
  const setJPData = useSetAtom(orgOnboardingJPDataAtom);
  const setJPStep = useSetAtom(orgOnboardingJPStepAtom);
  const setContactDetails = useSetAtom(orgOnboardingContactDetailsAtom);
  const setBinaryPictures = useSetAtom(orgOnboardingBinaryPicturesAtom);
  const setExistingUserFlow = useSetAtom(orgOnboardingExistingUserFlowAtom);
  const setExistingUserId = useSetAtom(orgOnboardingExistingUserIdAtom);
  const setExistingUserPhone = useSetAtom(orgOnboardingExistingUserPhoneAtom);
  const setConflictingFields = useSetAtom(orgOnboardingConflictingFieldsAtom);
  const setSaveAndContinueLater = useSetAtom(orgOnboardingSaveAndContinueLaterAtom);
  const setAiValidationIds = useSetAtom(orgOnboardingAiValidationIdsAtom);
  const setReferralInfo = useSetAtom(orgOnboardingReferralInfoAtom);
  const setReferralPrefill = useSetAtom(orgOnboardingReferralPrefillAtom);
  const setPartnerInfo = useSetAtom(orgOnboardingPartnerInfoAtom);
  const setProspectId = useSetAtom(orgOnboardingProspectIdAtom);

  const resetAll = useCallback(() => {
    setStep(ORG_ONBOARDING_STEPS[0]);
    setIsOrgMode(false);
    setRestaurantData(null);
    setJPData(null);
    setJPStep(null);
    setContactDetails(null);
    setBinaryPictures([]);
    setExistingUserFlow(false);
    setExistingUserId(null);
    setExistingUserPhone(null);
    setConflictingFields(null);
    setSaveAndContinueLater(false);
    setAiValidationIds([]);
    setReferralInfo(null);
    setReferralPrefill(null);
    setPartnerInfo(null);
    setProspectId(null);
    clearOtpCorrelationId();
  }, [
    setStep,
    setIsOrgMode,
    setRestaurantData,
    setJPData,
    setJPStep,
    setContactDetails,
    setBinaryPictures,
    setExistingUserFlow,
    setExistingUserId,
    setExistingUserPhone,
    setConflictingFields,
    setSaveAndContinueLater,
    setAiValidationIds,
    setReferralInfo,
    setReferralPrefill,
    setPartnerInfo,
    setProspectId,
  ]);

  return { resetAll };
};
